/**
 * Helper functions for handling images
 */

/**
 * Check if the browser supports WebP images
 */
export const isWebpSupported = (() => {
  try {
    if (typeof document === 'undefined') return false;
    const canvas = document.createElement('canvas');
    if (!!(canvas.getContext && canvas.getContext('2d'))) {
      return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    return false;
  } catch (e) {
    return false;
  }
})();

/**
 * Handle image errors when loading WebP images with fallbacks
 * to PNG and then JPG formats
 */
export const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  const webpPath = e.currentTarget.src;
  // Try to load the original PNG/JPG version
  if (webpPath.includes('/webp/')) {
    const originalPath = webpPath
      .replace('/webp/', '/')
      .replace('.webp', '.png');
    
    e.currentTarget.src = originalPath;
    
    // If that fails too, try JPG
    e.currentTarget.onerror = () => {
      e.currentTarget.src = originalPath.replace('.png', '.jpg');
      
      // If all fails, show a placeholder
      e.currentTarget.onerror = () => {
        e.currentTarget.src = '/images/placeholder-game.png';
        e.currentTarget.onerror = null;
      };
    };
  }
};

/**
 * Import a theatre app icon
 */
export const importAppIcon = (iconName: string) => {
  try {
    // Use a direct path to the assets folder
    return new URL(`../assets/appIcons/${iconName}`, import.meta.url).href;
  } catch (e) {
    console.warn(`Could not load icon: ${iconName}`, e);
    // Return a placeholder or default image
    return 'https://via.placeholder.com/150';
  }
};

/**
 * Extract embed ID from a thumbnail URL
 */
export const extractEmbedId = (thumbnailUrl: string): string => {
  // Try to extract the ID from the URL pattern
  const matches = thumbnailUrl.match(/(\d+)_[a-f0-9]+\.(?:png|jpg|webp)/);
  if (matches && matches[1]) {
    return matches[1];
  }
  
  // For previews like "mvscu.png" that don't follow the pattern
  const previewMatches = thumbnailUrl.match(/\/([^\/]+)\.(?:png|jpg|webp)$/);
  if (previewMatches && previewMatches[1]) {
    // Just return the filename without extension as a fallback
    return previewMatches[1];
  }
  
  // Default fallback
  return "game";
}; 